import { ChevronRight, Gavel, Target, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, isApiError } from '../../api';
import type { DropBackorder } from '../../api/areas/market';
import { Callout, DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { DomainLink } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatCount, formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { RefreshButton } from '../registry/shared';
import { AUCTION_STATUS, BACKORDER_STATUS, CATCH_STATUS, checkResult, CustomerRef, DROP_STATUS, MarketBadge, marketEnum, money } from './shared';

/** `/drops/:id`: one name we expect to drop — when, where the time came from, the catch attempt and who is queued. */
export function DropPage() {
  const { t } = useTranslation('market');
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getDrop(id, { signal }), [id]);
  const d = result.status === 'success' ? result.data : undefined;
  useDocumentTitle(d ? t('drop.docTitle', { domain: d.domain_name }) : t('drop.title'));
  const crumbs = [{ label: t('backorders.drops'), to: '/backorders?tab=drops' }, { label: d?.domain_name ?? '…' }];

  if (!d) {
    return (
      <>
        <PageHeader title={t('drop.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError error={result.error} onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload} title={t('drop.loadError')} />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }

  const attempt = d.attempt;
  const queueColumns: Column<DropBackorder>[] = [
    {
      key: 'customer',
      header: t('drop.customer'),
      className: 'max-w-56',
      cell: (b) => <CustomerRef id={b.customer_id} name={b.customer_name} />,
    },
    { key: 'level', header: t('drop.level'), cell: (b) => <span title={b.level}>{marketEnum('level', b.level)}</span> },
    { key: 'priority', header: t('drop.priority'), numeric: true, cell: (b) => b.priority_rank },
    { key: 'price', header: t('drop.price'), numeric: true, cell: (b) => <span className="whitespace-nowrap">{money(b.price, b.currency)}</span> },
    {
      key: 'status',
      header: t('drop.status'),
      className: 'min-w-36 max-w-60 whitespace-normal',
      cell: (b) => (
        <div className="space-y-1">
          <MarketBadge group="backorderStatus" map={BACKORDER_STATUS} value={b.status} />
          {b.status_reason ? <div className="text-xs text-muted-foreground">{b.status_reason}</div> : null}
        </div>
      ),
    },
    {
      key: 'placed',
      header: t('drop.placed'),
      cell: (b) => (
        <span className="whitespace-nowrap" title={formatDateTime(b.created_at)}>
          {formatRelative(b.created_at)}
        </span>
      ),
    },
    {
      key: 'go',
      header: <span className="sr-only">{t('drop.open', { customer: '' })}</span>,
      className: 'w-8',
      cell: (b) => (
        <Button asChild variant="ghost" size="icon-xs">
          <Link to={`/backorders/${encodeURIComponent(b.id)}`} aria-label={t('drop.open', { customer: b.customer_name ?? '' })}>
            <ChevronRight />
          </Link>
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={d.domain_name}
        crumbs={crumbs}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <MarketBadge group="dropStatus" map={DROP_STATUS} value={d.status} />
            <span>{formatRelative(d.expected_drop_at)}</span>
            <span aria-hidden="true">·</span>
            <span>{marketEnum('dropSource', d.source)}</span>
          </span>
        }
        actions={<RefreshButton onClick={result.reload} />}
      />

      <div className="space-y-6">
        <Section title={t('drop.details')}>
          <div className="space-y-4">
            <KeyValue
              columns={3}
              rows={[
                [t('drop.domain'), <DomainLink key="d" keyOrName={d.domain_name} />],
                [t('drop.registry'), d.registry_name],
                [t('drop.expected'), `${formatDateTime(d.expected_drop_at)} (${formatRelative(d.expected_drop_at)})`],
                [t('drop.source'), marketEnum('dropSource', d.source)],
                [t('drop.sourceReference'), d.source_reference],
                [t('drop.recorded'), formatDateTime(d.created_at)],
                [
                  t('drop.recordedBy'),
                  d.created_by_name ? <EntityRef key="rb" label={d.created_by_name} id={d.created_by_id} /> : marketEnum('actorType', d.created_by_type),
                ],
                [t('drop.updated'), formatDateTime(d.updated_at)],
                [t('drop.id'), <CopyText key="id" value={d.id} />],
              ]}
            />
            {d.status_reason ? (
              <Callout tone="info" title={t('drop.statusReason')}>
                {d.status_reason}
              </Callout>
            ) : null}
            <Callout tone="info">{t('backorders.dropTimesNote')}</Callout>
          </div>
        </Section>

        <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2 [&>*]:min-w-0">
          <Section
            title={
              <span className="flex items-center gap-2">
                <Target className="size-4 text-muted-foreground" aria-hidden="true" />
                {t('drop.attempt')}
              </span>
            }
          >
            {attempt ? (
              <KeyValue
                columns={2}
                rows={[
                  [t('drop.status'), <MarketBadge key="as" group="catchStatus" map={CATCH_STATUS} value={attempt.status} />],
                  [t('drop.attemptFor'), <CustomerRef key="c" id={attempt.customer_id} name={attempt.customer_name} />],
                  [t('drop.amount'), money(attempt.amount, attempt.currency)],
                  [t('drop.window'), `${formatDateTime(attempt.window_start)} → ${formatDateTime(attempt.window_end)}`],
                  [t('drop.checks'), <span key="ck" title={t('drop.interval', { ms: attempt.poll_interval_ms })}>{formatCount(attempt.checks_sent)}</span>],
                  [t('drop.lastCheck'), checkResult(attempt.last_check_result)],
                  [t('drop.outcome'), attempt.outcome_reason],
                  [t('drop.finished'), attempt.completed_at ? formatDateTime(attempt.completed_at) : null],
                  [
                    t('backorder.title'),
                    <EntityRef key="bo" label={t('auction.openBackorder')} id={attempt.backorder_id} to={`/backorders/${encodeURIComponent(attempt.backorder_id)}`} />,
                  ],
                ]}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{t('drop.noAttempt')}</p>
            )}
          </Section>

          <Section
            title={
              <span className="flex items-center gap-2">
                <Gavel className="size-4 text-muted-foreground" aria-hidden="true" />
                {t('drop.auction')}
              </span>
            }
          >
            {d.auction ? (
              <KeyValue
                columns={2}
                rows={[
                  [t('drop.status'), <MarketBadge key="s" group="auctionStatus" map={AUCTION_STATUS} value={d.auction.status} />],
                  [t('drop.winner'), <CustomerRef key="w" id={d.auction.winner_customer_id} name={d.auction.winner_name} />],
                  [t('drop.finalPrice'), d.auction.final_price ? money(d.auction.final_price, d.auction.currency) : null],
                  [t('drop.bids'), formatCount(d.auction.bid_count)],
                  [t('auctions.ends'), formatDateTime(d.auction.ends_at)],
                  [
                    t('drop.openAuction'),
                    <EntityRef key="l" label={t('drop.openAuction')} id={d.auction.id} to={`/auctions/${encodeURIComponent(d.auction.id)}`} />,
                  ],
                ]}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{t('drop.noAuction')}</p>
            )}
          </Section>
        </div>

        <Section
          flush
          title={
            <span className="flex items-center gap-2">
              <Users className="size-4 text-muted-foreground" aria-hidden="true" />
              {t('drop.queue')}
            </span>
          }
          description={t('drop.queueDesc')}
        >
          <DataTable columns={queueColumns} rows={d.backorders} rowKey={(b) => b.id} empty={<EmptyState title={t('drop.noQueue')} />} />
        </Section>
      </div>
    </>
  );
}
